/* eslint-disable react/no-danger */
import React, { useContext } from "react";
import Image from "components/image";
import Link from "helpers/link";
import { PortableText } from "@portabletext/react";
import getButtonStyle from "helpers/get-button-style";
import hasContainer from "helpers/has-container";
import getModulePadding from "helpers/get-module-padding";
import clean from "helpers/sanitize-html";
import { DynamicContentContext } from "@root/context/dynamic-content";
import richTextData from "./data";

// Two different stylings for a full module richtext
const templateClass = {
    Homepage: "homepage-richtext", Blog: "blog-richtext", NoStyle: "nostyle-richtext", 
};

const Video = ({ value }) => {
    const { controls, autoplay, muted, loop, video } = value;
    return (
        <video className="w-full" controls={controls} autoPlay={autoplay} muted={muted} loop={loop}>
            <track kind="captions" />
            <source src={video} type="video/mp4" />
            Your browser does not support HTML5 video.
        </video>
    );
};

const Code = (value) => {
    const { text } = value;
    const sanitizedHtml = clean(text);

    return <code dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

// Types
const ImageRenderer = ({ value: node }) => <Image className="mx-auto" image={node} alt={node.alt} to={node.url} />;

const ImageWithWidthRenderer = ({ value: node }) => (
    <div style={{ maxWidth: node.width }} className="mx-auto">
        <Image image={node} alt={node.alt} to={node.url} />
    </div>
);

const ImageWithHeightRenderer = ({ value: node }) => <Image image={node} alt={node.alt} to={node.url} style={{ maxHeight: node.height }} />;

const VideoEmbed = ({ value: node }) => {
    const alignMapping = {
        left: "justify-start",
        center: "justify-center",
        right: "justify-end",
    };

    const ratioMapping = {
        default: "pt-[56%]", 
        wide: "pt-[42%]",
        square: "pt-[75%]",
    };    

    return (
        <div className={`flex ${alignMapping[node.videoAlign]}`}>
            <div className="!w-full" style={{ maxWidth: node.maxWidth }}>
                <div className={`relative ${ratioMapping[node.ratio]}`}>
                    <iframe className="absolute top-0 bottom-0 left-0 right-0 !h-full !w-full" referrerPolicy="strict-origin-when-cross-origin" src={node.url} title={node.title} />
                </div>
            </div>
        </div>
    );
};

const ButtonRenderer = ({ value: node }) => (
    <Link className={getButtonStyle(node?.style)} to={node?.url}>
        {node?.text}
    </Link>
);

// Marks
const Links = ({ children, value }) => <Link to={value.href}>{children}</Link>;

const TextSettings = ({ children, value }) => (
    <span 
        className={`${value?.extraClasses ? value?.extraClasses : ""}`}
        id={`${value?.id ? value?.id : ""}`}
        style={{
            display: "block",
            color: value?.color?.hex,
            textAlign: value?.alignment, 
        }}
    >
        {children}
    </span>
);

const NestedBlock = ({ value }, template) => {
    const types = {
        types: {
            imageWithoutWidth: ImageRenderer,
            ImageWithWidth: ImageWithWidthRenderer,
            ImageWithHeight: ImageWithHeightRenderer,
            video: Video,
            videoEmbed: VideoEmbed,
            ...template?.types,
        },
        marks: {
            link: Links,
            code: Code,
            ...template?.marks,
        },
        block: { ...template?.block },
        list: { ...template?.list },
        listItem: { ...template?.listItem },
    };
    
    const computeWidthRatio = (ratio, isLeft) => {
        if (!ratio || typeof ratio !== "string") {
            return isLeft ? "2/5" : "3/5";
        }

        switch (ratio) {
        case "right70":
            return isLeft ? "3/10" : "7/10";
        case "default":
            return isLeft ? "2/5" : "3/5";
        case "equal":
            return "1/2";
        case "counterDefault":
            return isLeft ? "3/5" : "2/5";
        case "left70":
            return isLeft ? "7/10" : "3/10";
        default:
            return isLeft ? "2/5" : "3/5";
        }
    };

    return (
        <div className={`flex lg:flex-row ${value.mobileFlexReverse ? "flex-col-reverse" : "flex-col"}`}>
            {value.leftBlock?.length > 0 && (
                <div className={`w-full lg:w-${computeWidthRatio(value.blocksWidthRatio, true)} blog-richtext full-module-richtext px-0 lg:pr-4 mb-4 lg:mb-8`}>
                    <PortableText
                        value={value.leftBlock}
                        components={types}
                    />
                </div>
            )}
            {value.rightBlock?.length > 0 && (
                <div className={`w-full lg:w-${computeWidthRatio(value.blocksWidthRatio, false)} blog-richtext full-module-richtext px-0 lg:px-4 mb-4 lg:mb-8`}>
                    <PortableText
                        value={value.rightBlock}
                        components={types}
                    />
                </div>
            )}
        </div>
    );
};

export const richTextComponents = (template) => ({
    types: {
        Image: ImageRenderer,
        ImageWithWidth: ImageWithWidthRenderer,
        ImageWithHeight: ImageWithHeightRenderer,
        videoEmbed: VideoEmbed,
        Button: ButtonRenderer,
        Video,
        ...template?.types,
        nestedBlock: (props) => NestedBlock(props, template),
    },
    marks: {
        link: Links,
        textSettings: TextSettings,
        code: Code,
        ...template?.marks,
    },
    block: { ...template?.block },
    list: { ...template?.list },
    listItem: { ...template?.listItem },
});

const RichText = ({ value, data, ...other }) => {
    const { getDynamicContent } = useContext(DynamicContentContext) || {};

    const dynamicContent = getDynamicContent?.(data.dynamicContentPath);

    return (
        <div className={`
            full-module-richtext
            ${hasContainer(data?.moduleSettings?.hasContainer) !== "container" ? "p-0" : "container"}
            ${templateClass[data?.template]}
        `}
        >
            <div className={`${data?.hasInternalPadding && getModulePadding()} richText-description`}>
                {data?.hasBorderTop
                    && <div className="w-full h-[0.019rem] shadow-richTextBoxShadow bg-black opacity-25 mb-10" />}

                {dynamicContent && typeof dynamicContent === "string" && (
                    <div dangerouslySetInnerHTML={{ __html: dynamicContent }} />
                )}

                {!dynamicContent && (
                    <PortableText
                        value={value ?? data?.data}
                        components={richTextComponents(richTextData[data?.template])}
                        {...other}
                    />
                )}
            </div>
        </div>
    );
};

export default RichText;
